// Search parameters of each route, parsed by hand: anything unexpected is dropped, never trusted.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
/** The user.hash the app passes to setUser, as the API takes it (packages/clickhouse app-user.ts). */
export const APP_USER_HASH = /^[A-Za-z0-9+/=_.:-]{1,256}$/

// The router parses search values as JSON, so a release named 100 arrives as a number.
const str = (v: unknown, max = 128): string | undefined => {
  const t = typeof v === 'number' && Number.isFinite(v) ? String(v) : v
  return typeof t === 'string' && t.length > 0 && t.length <= max ? t : undefined
}

/**
 * The time range the views share (APS-195): one `range` in the URL, carried from view to view
 * with the app and the release. `1h` is the dashboards' own; each view clamps the range to the
 * ones it has (clampRange).
 */
export const SCOPE_RANGES = ['1h', '24h', '7d', '30d', '90d'] as const
export type ScopeRange = (typeof SCOPE_RANGES)[number]

export const RANGE_DAYS: Record<ScopeRange, number> = {
  '1h': 1 / 24,
  '24h': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
}

/** Each view's ranges, shortest first, and the one it opens with. */
export const VIEW_RANGES = {
  home: { ranges: ['24h', '7d', '30d'], fallback: '7d' },
  releases: { ranges: ['30d'], fallback: '30d' },
  issues: { ranges: ['24h', '7d', '30d', '90d'], fallback: '7d' },
  sessions: { ranges: ['24h', '7d', '30d'], fallback: '7d' },
  // The flags read keeps to 30 days (D62).
  flags: { ranges: ['24h', '7d', '30d'], fallback: '7d' },
  // Logs per hour: 24 or 168 bars (APS-327).
  logs: { ranges: ['24h', '7d'], fallback: '7d' },
  perf: { ranges: ['24h', '7d', '30d'], fallback: '7d' },
  dashboards: { ranges: ['1h', '24h', '7d', '30d'], fallback: '24h' },
} as const satisfies Record<string, { ranges: readonly ScopeRange[]; fallback: ScopeRange }>
export type RangeView = keyof typeof VIEW_RANGES

/**
 * The range a view shows for the URL's: the longest it has that is not longer, or its shortest.
 * None in the URL is the view's own default.
 */
export function clampRange(range: ScopeRange | undefined, view: RangeView): ScopeRange {
  const { ranges, fallback } = VIEW_RANGES[view]
  if (!range) return fallback
  const fits = ranges.filter((r) => RANGE_DAYS[r] <= RANGE_DAYS[range])
  return fits[fits.length - 1] ?? ranges[0]
}

/** A view's range in days, as the read API takes it. */
export const rangeDays = (range: ScopeRange | undefined, view: RangeView): number =>
  RANGE_DAYS[clampRange(range, view)]

/** `range`, or a link from before it: `days` becomes the shortest range that covers it. */
function parseRange(s: Record<string, unknown>): { range?: ScopeRange } {
  if ((SCOPE_RANGES as readonly unknown[]).includes(s.range))
    return { range: s.range as ScopeRange }
  const days = Number(s.days)
  if (!Number.isInteger(days) || days < 1 || days > 90) return {}
  const range = (['24h', '7d', '30d', '90d'] as const).find((r) => RANGE_DAYS[r] >= days)
  return range ? { range } : {}
}

export interface ScopeSearch {
  /** The app in scope (D6); none means every app of the project. */
  app?: string | undefined
  /** The release in scope (service.version), carried across views with the app. */
  release?: string | undefined
  range?: ScopeRange | undefined
  /**
   * One device model and one exact OS version (APS-228), carried across views with the release:
   * the bar's '+ Filter' sets them, Sessions' own chips read the same keys (APS-400).
   */
  device?: string | undefined
  os?: string | undefined
  /**
   * Which builds (D6, APS-508): absent is release builds, the default; `debug` or `all` only when
   * chosen, so a link at the default stays as it was. Carried across views like the release.
   */
  build?: ScopeBuild | undefined
}

/** The builds a view can show besides the default, release ones (D6, APS-508). */
export const SCOPE_BUILDS = ['debug', 'all'] as const
export type ScopeBuild = (typeof SCOPE_BUILDS)[number]

/** The scope every project view keeps: app, release, range, device and OS (APS-195, APS-228). */
export function parseScopeSearch(s: Record<string, unknown>): ScopeSearch {
  const app = str(s.app)
  const release = str(s.release)
  const device = str(s.device)
  const os = str(s.os, 64)
  return {
    ...(app && UUID.test(app) ? { app } : {}),
    ...(release ? { release } : {}),
    ...parseRange(s),
    ...(device ? { device } : {}),
    ...(os ? { os } : {}),
    ...((SCOPE_BUILDS as readonly unknown[]).includes(s.build)
      ? { build: s.build as ScopeBuild }
      : {}),
  }
}

/** What a link to another view carries: the scope, without the view's own filters. */
export function carried(s: ScopeSearch): ScopeSearch {
  return {
    ...(s.app ? { app: s.app } : {}),
    ...(s.release ? { release: s.release } : {}),
    ...(s.range ? { range: s.range } : {}),
    ...(s.device ? { device: s.device } : {}),
    ...(s.os ? { os: s.os } : {}),
    ...(s.build ? { build: s.build } : {}),
  }
}

/**
 * The scope's device and build filters as the read API takes them (APS-228, APS-508). Release
 * builds, the default, send nothing: the API's default is the same.
 */
export function deviceParams(s: ScopeSearch): {
  device_model?: string
  os_version?: string
  build?: ScopeBuild
} {
  return {
    ...(s.device ? { device_model: s.device } : {}),
    ...(s.os ? { os_version: s.os } : {}),
    ...(s.build ? { build: s.build } : {}),
  }
}

export type ReleasesSearch = ScopeSearch

/** The releases list (APS-307): the pair the comparison shows, newer `cand` against `base`. */
export interface ReleasesListSearch extends ReleasesSearch {
  cand?: string | undefined
  base?: string | undefined
}

/** A person's page (APS-547): the scope, and the Live tab when it is the one open. */
export interface PersonSearch extends ScopeSearch {
  tab?: 'live' | undefined
}

export function parsePersonSearch(s: Record<string, unknown>): PersonSearch {
  return { ...parseScopeSearch(s), ...(s.tab === 'live' ? { tab: 'live' as const } : {}) }
}

export const parseReleasesSearch = parseScopeSearch

export function parseReleasesListSearch(s: Record<string, unknown>): ReleasesListSearch {
  const cand = str(s.cand, 128)
  const base = str(s.base, 128)
  return { ...parseScopeSearch(s), ...(cand ? { cand } : {}), ...(base ? { base } : {}) }
}

/** Issue kinds the read API groups (D73). */
export const ISSUE_KINDS = ['crash', 'error', 'anr', 'hang', 'abnormal_exit'] as const
export type IssueKindSearch = (typeof ISSUE_KINDS)[number]

/** A feature flag and its variant (step 3.6), as filters. */
export interface FlagSearch {
  flag?: string | undefined
  variant?: string | undefined
}

/** The flag filter: a variant only with its flag. */
export function parseFlagSearch(s: Record<string, unknown>): FlagSearch {
  const flag = str(s.flag)
  const variant = str(s.variant)
  return flag ? { flag, ...(variant ? { variant } : {}) } : {}
}

/**
 * The issues list's views besides Unresolved, the default (D100, APS-299): new in the release,
 * regressed, mine (assigned to the person asking), resolved, ignored, and every issue whatever
 * its state.
 */
export const ISSUE_TABS = ['new', 'regressed', 'mine', 'resolved', 'ignored', 'all'] as const
export type IssueTab = (typeof ISSUE_TABS)[number]

/** The columns the list sorts by (APS-299), most first unless `order` is asc. */
export const ISSUE_SORTS = [
  'status',
  'kind',
  'sessions',
  'users',
  'first_seen',
  'last_seen',
] as const
export type IssueSortSearch = (typeof ISSUE_SORTS)[number]

export interface IssuesSearch extends ReleasesSearch, FlagSearch {
  kind?: IssueKindSearch | undefined
  tab?: IssueTab | undefined
  sort?: IssueSortSearch | undefined
  order?: 'asc' | undefined
  /** Assigned to the person asking, or to nobody. */
  assignee?: 'me' | 'none' | undefined
}

export function parseIssuesSearch(s: Record<string, unknown>): IssuesSearch {
  return {
    ...parseReleasesSearch(s),
    ...((ISSUE_KINDS as readonly unknown[]).includes(s.kind)
      ? { kind: s.kind as IssueKindSearch }
      : {}),
    ...((ISSUE_TABS as readonly unknown[]).includes(s.tab) ? { tab: s.tab as IssueTab } : {}),
    ...((ISSUE_SORTS as readonly unknown[]).includes(s.sort)
      ? { sort: s.sort as IssueSortSearch }
      : {}),
    ...(s.order === 'asc' ? { order: 'asc' as const } : {}),
    ...(s.assignee === 'me' || s.assignee === 'none' ? { assignee: s.assignee } : {}),
    ...parseFlagSearch(s),
  }
}

/**
 * Settings' sections, one page each (/settings/$section, Console D's settings mode): the
 * project's (general, apps and keys, symbols, issue grouping, APM link, agents and tokens, data
 * export), the
 * organization's (alerts and their destinations, members, plan, activity, data and privacy) and
 * the account's (profile, preferences).
 */
export const SETTINGS_SECTIONS = [
  'general',
  'apps',
  'symbols',
  'grouping',
  'apm',
  'agents',
  'data',
  'alerts',
  'members',
  'integrations',
  'plan',
  'activity',
  'privacy',
  'profile',
  'preferences',
] as const
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]

export const isSettingsSection = (v: unknown): v is SettingsSection =>
  (SETTINGS_SECTIONS as readonly unknown[]).includes(v)

export interface SettingsSearch extends ScopeSearch {
  /** Agent handoff (step 4.3): the connection GitHub or Linear just made, to choose from. */
  handoff?: string | undefined
  /** Why connecting GitHub or Linear did not finish (a code from the API's callback). */
  handoff_error?: string | undefined
  /** Agents and tokens: the OAuth client an agent's refused write named, to grant (D98);
   * untrusted, shown as text. */
  client?: string | undefined
  /** What the refused write needed (APS-225), to preselect. */
  capability?: AgentCapability | undefined
}

/** What an agent grant allows (D98, APS-225, D117, D115), as the API names it. */
export const AGENT_CAPABILITIES = [
  'dashboards:write',
  'issues:write',
  'instrumentation:write',
  'exports:write',
] as const
export type AgentCapability = (typeof AGENT_CAPABILITIES)[number]

const UUID_PARAM = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const ERROR_CODE = /^[a-z_]{1,32}$/
/** An OAuth client_id, as the API keeps it (agent_grants): printable ASCII, 1 to 256. */
const CLIENT_ID = /^[!-~]{1,256}$/

export function parseSettingsSearch(s: Record<string, unknown>): SettingsSearch {
  return {
    ...parseScopeSearch(s),
    ...(typeof s.handoff === 'string' && UUID_PARAM.test(s.handoff) ? { handoff: s.handoff } : {}),
    ...(typeof s.handoff_error === 'string' && ERROR_CODE.test(s.handoff_error)
      ? { handoff_error: s.handoff_error }
      : {}),
    ...(typeof s.client === 'string' && CLIENT_ID.test(s.client) ? { client: s.client } : {}),
    ...((AGENT_CAPABILITIES as readonly unknown[]).includes(s.capability)
      ? { capability: s.capability as AgentCapability }
      : {}),
  }
}

export const SESSION_OUTCOMES = ['crashed', 'abnormal_exit', 'ok'] as const
const ISSUE_ID = /^[A-Za-z0-9._:-]{1,128}$/

export interface SessionsSearch extends ReleasesSearch, EndpointParams, FlagSearch {
  outcome?: (typeof SESSION_OUTCOMES)[number] | undefined
  device?: string | undefined
  os?: string | undefined
  errors?: boolean | undefined
  /** Only sessions with a stored replay (APS-196). */
  replay?: boolean | undefined
  issue?: string | undefined
  /** With an endpoint: only sessions where a request to it failed (step 3.4). */
  failed?: boolean | undefined
  /** Sessions that loaded this screen, with a TTID of at least min_ttid ms or a TTFD of at
   * least min_ttfd ms (APS-301). */
  screen?: string | undefined
  min_ttid?: number | undefined
  min_ttfd?: number | undefined
  /** Sessions with a failed request to any endpoint (APS-301). */
  failed_request?: boolean | undefined
  /** Sessions whose last screen was this one: where they stopped (Journeys, APS-301). */
  stopped?: string | undefined
  /** One app user's sessions, by the user.hash the app sends (People, D114). */
  user?: string | undefined
  /** The list's sort (APS-301); started, newest first, when absent. */
  sort?: (typeof SESSION_SORTS)[number] | undefined
  order?: 'asc' | undefined
}

export const SESSION_SORTS = ['started', 'length', 'errors'] as const

/** The sessions list's filters, as the URL keeps them (shareable, back-button friendly). */
export function parseSessionsSearch(s: Record<string, unknown>): SessionsSearch {
  const device = str(s.device)
  const os = str(s.os, 64)
  const issue = str(s.issue)
  const stopped = str(s.stopped, 256)
  const user = str(s.user, 256)
  return {
    ...parseReleasesSearch(s),
    ...((SESSION_OUTCOMES as readonly unknown[]).includes(s.outcome)
      ? { outcome: s.outcome as SessionsSearch['outcome'] }
      : {}),
    ...(device ? { device } : {}),
    ...(os ? { os } : {}),
    ...(s.errors === true || s.errors === 'true' ? { errors: true } : {}),
    ...(s.errors === false || s.errors === 'false' ? { errors: false } : {}),
    ...(s.replay === true || s.replay === 'true' ? { replay: true } : {}),
    ...(issue && ISSUE_ID.test(issue) ? { issue } : {}),
    ...parseEndpoint(s),
    ...(s.failed === true || s.failed === 'true' ? { failed: true } : {}),
    ...(s.failed_request === true || s.failed_request === 'true' ? { failed_request: true } : {}),
    ...parseScreen(s),
    ...parseFlagSearch(s),
    ...(stopped ? { stopped } : {}),
    ...(user && APP_USER_HASH.test(user) ? { user } : {}),
    ...(s.sort === 'length' || s.sort === 'errors' ? { sort: s.sort } : {}),
    ...(s.order === 'asc' ? { order: 'asc' as const } : {}),
  }
}

// Logs (APS-327; Console D `#/logs`)

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error', 'fatal'] as const
export type LogLevel = (typeof LOG_LEVELS)[number]

/**
 * The Logs page's query, as the URL keeps it: a level and above, a logger (the record's scope),
 * a screen, an OS and major version (`iOS 26`, key `os_major`), a session, and words in the body.
 * The release is the scope's chip.
 */
export interface LogsSearch extends ScopeSearch {
  level?: LogLevel | undefined
  logger?: string | undefined
  screen?: string | undefined
  /**
   * The `os:` token, an OS name and major version (`iOS 26`). Its own key: `os` is the scope's
   * exact OS version (APS-228), which Logs does not apply.
   */
  os_major?: string | undefined
  session?: string | undefined
  q?: string | undefined
}

/** `iOS 26`, `Android 15`: an OS name and a major version. */
export const LOG_OS = /^(\S.{0,62}) (\d{1,4})$/

export function parseLogsSearch(s: Record<string, unknown>): LogsSearch {
  const logger = str(s.logger, 256)
  const screen = str(s.screen, 256)
  const os = str(s.os_major, 64)
  const session = str(s.session)
  const q = str(s.q, 200)
  return {
    ...parseScopeSearch(s),
    ...((LOG_LEVELS as readonly unknown[]).includes(s.level) ? { level: s.level as LogLevel } : {}),
    ...(logger ? { logger } : {}),
    ...(screen ? { screen } : {}),
    ...(os && LOG_OS.test(os) ? { os_major: os } : {}),
    ...(session ? { session } : {}),
    ...(q?.trim() ? { q: q.trim() } : {}),
  }
}

// Performance and network (plan step 3.4)

export type PerfSearch = ReleasesSearch

export const parsePerfSearch = parseReleasesSearch

/** App start's breakdowns, in the order the page offers them (Console D). */
export const START_GROUPS = ['release', 'device_model', 'os_version', 'prewarmed'] as const
export const START_TYPES = ['cold', 'warm', 'hot'] as const
export interface StartSearch extends PerfSearch {
  by?: (typeof START_GROUPS)[number] | undefined
  /** The start type the breakdown and its histogram show; cold when absent. */
  type?: (typeof START_TYPES)[number] | undefined
}

export function parseStartSearch(s: Record<string, unknown>): StartSearch {
  return {
    ...parsePerfSearch(s),
    ...((START_GROUPS as readonly unknown[]).includes(s.by)
      ? { by: s.by as StartSearch['by'] }
      : {}),
    ...((START_TYPES as readonly unknown[]).includes(s.type) && s.type !== 'cold'
      ? { type: s.type as StartSearch['type'] }
      : {}),
  }
}

export const SCREEN_SORTS = ['loads', 'ttid_p90', 'ttfd_p90'] as const
export const ENDPOINT_SORTS = ['requests', 'error_rate', 'p90'] as const
export const FRAME_SORTS = ['minutes', 'slow_per_minute', 'frozen_per_minute'] as const
export interface ListSearch extends PerfSearch {
  sort?: string | undefined
}

export function parseScreensSearch(s: Record<string, unknown>): ListSearch {
  return {
    ...parsePerfSearch(s),
    ...((SCREEN_SORTS as readonly unknown[]).includes(s.sort) ? { sort: s.sort as string } : {}),
  }
}

export function parseNetworkSearch(s: Record<string, unknown>): ListSearch {
  return {
    ...parsePerfSearch(s),
    ...((ENDPOINT_SORTS as readonly unknown[]).includes(s.sort) ? { sort: s.sort as string } : {}),
  }
}

export function parseFramesSearch(s: Record<string, unknown>): ListSearch {
  return {
    ...parsePerfSearch(s),
    ...((FRAME_SORTS as readonly unknown[]).includes(s.sort) ? { sort: s.sort as string } : {}),
  }
}

/** A screen name: the app's own text, up to 256 characters. */
function parseScreen(s: Record<string, unknown>): {
  screen?: string
  min_ttid?: number
  min_ttfd?: number
} {
  const screen = str(s.screen, 256)
  const ms = (v: unknown) => {
    const n = Number(v)
    return Number.isInteger(n) && n >= 0 && n <= 600_000 ? n : undefined
  }
  const ttid = ms(s.min_ttid)
  const ttfd = ms(s.min_ttfd)
  return screen
    ? {
        screen,
        ...(ttid !== undefined ? { min_ttid: ttid } : {}),
        ...(ttfd !== undefined ? { min_ttfd: ttfd } : {}),
      }
    : {}
}

export interface ScreenSearch extends PerfSearch {
  screen?: string | undefined
}

export function parseScreenSearch(s: Record<string, unknown>): ScreenSearch {
  const screen = str(s.screen, 256)
  return { ...parsePerfSearch(s), ...(screen ? { screen } : {}) }
}

/** An endpoint as the network views name it: method, host and template (spec 8.4). */
export interface EndpointParams {
  method?: string | undefined
  host?: string | undefined
  template?: string | undefined
}

function parseEndpoint(s: Record<string, unknown>): EndpointParams {
  const method = str(s.method, 16)
  const host = str(s.host, 255)
  const template = str(s.template, 256)
  return method && /^[A-Z_]{1,16}$/.test(method) && host && template
    ? { method, host, template }
    : {}
}

export interface EndpointSearch extends PerfSearch, EndpointParams {}

export function parseEndpointSearch(s: Record<string, unknown>): EndpointSearch {
  return { ...parsePerfSearch(s), ...parseEndpoint(s) }
}

export interface InvitationSearch {
  /** The invitation's token, from the email's link (APS-274). */
  token?: string | undefined
}

export function parseInvitationSearch(s: Record<string, unknown>): InvitationSearch {
  const token = str(s.token, 200)
  return token && /^[A-Za-z0-9_-]+$/.test(token) ? { token } : {}
}

export interface SignInSearch {
  return_to?: string | undefined
  /** `sign-up`: someone new, sent by /signup; the sign-up path comes first. */
  screen?: 'sign-up' | undefined
  /** Why the API sent a refused sign-in back here (APS-249): its address's limit. */
  error?: 'login_rate_limited' | 'network_rate_limited' | undefined
  /** When to try again, in seconds (the refusal's Retry-After). */
  retry?: number | undefined
}

/** Only a path inside the console: never an open redirect. */
export function parseSignInSearch(s: Record<string, unknown>): SignInSearch {
  const r = str(s.return_to, 2000)
  const seconds = Number(s.retry)
  const retry = Number.isInteger(seconds) && seconds > 0 && seconds <= 86_400 ? seconds : undefined
  return {
    ...(r?.startsWith('/') && !r.startsWith('//') && !r.startsWith('/\\') ? { return_to: r } : {}),
    ...(s.screen === 'sign-up' ? { screen: 'sign-up' as const } : {}),
    ...(s.error === 'login_rate_limited' || s.error === 'network_rate_limited'
      ? { error: s.error }
      : {}),
    ...(retry !== undefined ? { retry } : {}),
  }
}

export interface PlanSearch {
  /**
   * The new organization got no trial (APS-249): its domain's or the account's was used, and it
   * is on Free (D107). The project step says so, and onboarding ends on Settings › Plan.
   */
  trial?: 'domain_used' | 'account_used' | undefined
  /** The domain whose trial was used, to say so. */
  domain?: string | undefined
}

const DOMAIN = /^[a-z0-9.-]{1,253}$/

export function parsePlanSearch(s: Record<string, unknown>): PlanSearch {
  const domain = str(s.domain, 253)
  return {
    ...(s.trial === 'domain_used' || s.trial === 'account_used' ? { trial: s.trial } : {}),
    ...(domain && DOMAIN.test(domain) ? { domain } : {}),
  }
}

// Funnels and journeys (plan step 3.6)

export const FUNNEL_WITHIN = [5, 30, 60, 1440] as const
export const JOURNEY_DEPTHS = [1, 2, 3, 4] as const

export interface JourneysSearch extends PerfSearch {
  /** The funnel's screens, in order: 2 to 6 once complete, kept while it is being built. */
  steps?: string[] | undefined
  within?: (typeof FUNNEL_WITHIN)[number] | undefined
  /** The paths' screen: both ways from it are shown. */
  screen?: string | undefined
  depth?: (typeof JOURNEY_DEPTHS)[number] | undefined
}

export function parseJourneysSearch(s: Record<string, unknown>): JourneysSearch {
  const steps = Array.isArray(s.steps)
    ? s.steps.slice(0, 6).map((v) => str(v, 256) ?? '')
    : undefined
  const screen = str(s.screen, 256)
  const within = Number(s.within)
  const depth = Number(s.depth)
  return {
    ...parsePerfSearch(s),
    ...(steps?.length ? { steps } : {}),
    ...((FUNNEL_WITHIN as readonly number[]).includes(within)
      ? { within: within as JourneysSearch['within'] }
      : {}),
    ...(screen ? { screen } : {}),
    ...((JOURNEY_DEPTHS as readonly number[]).includes(depth)
      ? { depth: depth as JourneysSearch['depth'] }
      : {}),
  }
}

export interface DashboardsSearch extends ScopeSearch {
  /** The board shown; none means the first. */
  board?: string | undefined
}

export function parseDashboardsSearch(s: Record<string, unknown>): DashboardsSearch {
  const board = str(s.board)
  return {
    ...parseScopeSearch(s),
    ...(board && UUID.test(board) ? { board } : {}),
  }
}

export const EXPLORE_TABS = ['sql', 'metrics', 'traces'] as const
/** Explore › Traces' orders: newest first (the default), or longest first. */
export const SPAN_SORTS = ['recent', 'slowest'] as const
export type SpanSort = (typeof SPAN_SORTS)[number]
export const EXPLORE_RANGES = ['1h', '24h', '7d', '30d'] as const
export type ExploreRange = (typeof EXPLORE_RANGES)[number]
export const METRIC_KINDS = ['sum', 'gauge', 'histogram'] as const
export type MetricKind = (typeof METRIC_KINDS)[number]
export const AGGREGATIONS = [
  'count',
  'rate',
  'sum',
  'avg',
  'min',
  'max',
  'p50',
  'p90',
  'p95',
  'p99',
] as const
export type Aggregation = (typeof AGGREGATIONS)[number]

export interface ExploreSearch extends Omit<ScopeSearch, 'range'> {
  /** SQL (the default), Metrics or Traces (APS-198); Logs is its own page (APS-327). */
  tab?: (typeof EXPLORE_TABS)[number] | undefined
  /** The query bar's words (lib/logQuery.ts, lib/spanQuery.ts), as typed: logs, spans, or a metric's filters. */
  q?: string | undefined
  range?: ExploreRange | undefined
  /** Metrics: the metric, its kind, the aggregation and the field it is split by. */
  metric?: string | undefined
  kind?: MetricKind | undefined
  agg?: Aggregation | undefined
  by?: string | undefined
  /** Traces: slowest first; newest first without it. */
  sort?: 'slowest' | undefined
}

const LABEL = /^[A-Za-z0-9_.:/-]{1,255}$/
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  (list as readonly unknown[]).includes(v) ? (v as T) : undefined

export function parseExploreSearch(s: Record<string, unknown>): ExploreSearch {
  const q = str(s.q, 1000)
  const metric = str(s.metric, 255)
  const by = str(s.by)
  const tab = s.tab === 'sql' ? undefined : oneOf(EXPLORE_TABS, s.tab)
  const range = oneOf(EXPLORE_RANGES, s.range)
  const kind = oneOf(METRIC_KINDS, s.kind)
  const agg = oneOf(AGGREGATIONS, s.agg)
  const sort = s.sort === 'slowest' ? 'slowest' : undefined
  // Explore's ranges are its own (detail data, at most 30 days): a carried 90d is dropped.
  const { range: _carried, ...scope } = parseScopeSearch(s)
  return {
    ...scope,
    ...(tab ? { tab } : {}),
    ...(q ? { q } : {}),
    ...(range ? { range } : {}),
    ...(metric && LABEL.test(metric) ? { metric } : {}),
    ...(kind ? { kind } : {}),
    ...(agg ? { agg } : {}),
    ...(by && LABEL.test(by) ? { by } : {}),
    ...(sort ? { sort } : {}),
  }
}

/** Alerts' tabs (Console D's #/alerts): what fires now, the rules, and what was sent. */
export const ALERT_TABS = ['firing', 'rules', 'history'] as const
/** The rules tab's state filter. */
export const ALERT_STATES = ['firing', 'at_risk', 'ok', 'muted', 'off'] as const

export interface AlertsSearch extends ScopeSearch {
  /** The tab; firing when absent. */
  tab?: (typeof ALERT_TABS)[number] | undefined
  state?: (typeof ALERT_STATES)[number] | undefined
  /** Only the rules the person asking created (APS-303). */
  mine?: boolean | undefined
}

export function parseAlertsSearch(s: Record<string, unknown>): AlertsSearch {
  return {
    ...parseScopeSearch(s),
    ...(s.tab === 'rules' || s.tab === 'history' ? { tab: s.tab } : {}),
    ...((ALERT_STATES as readonly unknown[]).includes(s.state)
      ? { state: s.state as AlertsSearch['state'] }
      : {}),
    ...(s.mine === true || s.mine === 'true' ? { mine: true } : {}),
  }
}
